import React from 'react';
import { Card, Typography, Space, Tag } from 'antd';
import { CheckOutlined } from '@ant-design/icons';

const { Text, Title } = Typography;

interface ThemePreviewProps {
  theme: {
    name: string;
    primaryColor: string;
    secondaryColor: string;
    accentColor: string;
    backgroundColor: string;
    surfaceColor: string;
    fontFamily: string;
    headerBg: string;
    footerBg: string;
    textColor: string;
    textMuted: string;
    linkColor: string;
    borderColor: string;
    shadowColor: string;
    gradientPrimary: string;
    gradientSecondary: string;
    cardStyle: 'flat' | 'elevated' | 'outlined' | 'glass';
    layout: 'grid' | 'masonry' | 'list';
    animation: 'none' | 'subtle' | 'dynamic';
  };
  isSelected?: boolean;
  onClick?: () => void;
}

const ThemePreview: React.FC<ThemePreviewProps> = ({ theme, isSelected, onClick }) => {
  const getCardStyle = () => {
    const baseStyle: React.CSSProperties = {
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      border: isSelected ? `2px solid ${theme.primaryColor}` : `1px solid ${theme.borderColor}`,
      backgroundColor: theme.surfaceColor,
      color: theme.textColor,
      fontFamily: theme.fontFamily,
    };

    if (theme.cardStyle === 'elevated') {
      baseStyle.boxShadow = `0 4px 12px ${theme.shadowColor}`;
    } else if (theme.cardStyle === 'outlined') {
      baseStyle.border = `2px solid ${theme.primaryColor}`;
    } else if (theme.cardStyle === 'glass') {
      baseStyle.background = `linear-gradient(135deg, ${theme.surfaceColor}88, ${theme.surfaceColor}44)`;
      baseStyle.backdropFilter = 'blur(10px)';
    }

    if (isSelected) {
      baseStyle.transform = 'scale(1.02)';
      baseStyle.boxShadow = `0 8px 24px ${theme.shadowColor}`;
    }

    return baseStyle;
  };

  const getGradientStyle = () => ({
    background: theme.gradientPrimary,
    height: '60px',
    borderRadius: '8px 8px 0 0',
    margin: '-1px -1px 16px -1px',
  });

  return (
    <Card
      hoverable
      style={getCardStyle()}
      onClick={onClick}
      styles={{
        body: { padding: '16px' }
      }}
    >
      {isSelected && (
        <div style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          backgroundColor: theme.primaryColor,
          color: 'white',
          borderRadius: '50%',
          width: '24px',
          height: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '12px',
        }}>
          <CheckOutlined />
        </div>
      )}
      
      {/* Header with gradient */}
      <div style={getGradientStyle()} />
      
      {/* Theme name */}
      <Title level={5} style={{ 
        margin: '0 0 12px 0', 
        color: theme.textColor,
        textAlign: 'center'
      }}>
        {theme.name.charAt(0).toUpperCase() + theme.name.slice(1)} Theme
      </Title>

      {/* Color palette */}
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
          <div style={{
            width: '20px',
            height: '20px',
            backgroundColor: theme.primaryColor,
            borderRadius: '4px',
            border: `1px solid ${theme.borderColor}`
          }} />
          <div style={{
            width: '20px',
            height: '20px',
            backgroundColor: theme.secondaryColor,
            borderRadius: '4px',
            border: `1px solid ${theme.borderColor}`
          }} />
          <div style={{
            width: '20px',
            height: '20px',
            backgroundColor: theme.accentColor,
            borderRadius: '4px',
            border: `1px solid ${theme.borderColor}`
          }} />
        </div>

        {/* Style tags */}
        <div style={{ textAlign: 'center' }}>
          <Tag 
            color={theme.primaryColor} 
            style={{ 
              color: 'white', 
              fontSize: '10px',
              margin: '2px'
            }}
          >
            {theme.cardStyle}
          </Tag>
          <Tag 
            color={theme.secondaryColor} 
            style={{ 
              color: 'white', 
              fontSize: '10px',
              margin: '2px'
            }}
          >
            {theme.layout}
          </Tag>
          <Tag 
            color={theme.accentColor} 
            style={{ 
              color: 'white', 
              fontSize: '10px',
              margin: '2px'
            }}
          >
            {theme.animation}
          </Tag>
        </div>

        {/* Sample content */}
        <div style={{
          backgroundColor: theme.backgroundColor,
          padding: '8px',
          borderRadius: '4px',
          border: `1px solid ${theme.borderColor}`,
          marginTop: '8px'
        }}>
          <Text style={{ 
            color: theme.textColor, 
            fontSize: '12px',
            display: 'block',
            marginBottom: '4px'
          }}>
            Sample Content
          </Text>
          <Text style={{ 
            color: theme.textMuted, 
            fontSize: '10px',
            display: 'block'
          }}>
            This is how your website will look
          </Text>
          <button 
            style={{ 
              color: theme.linkColor, 
              fontSize: '10px',
              textDecoration: 'none',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0
            }}
          >
            Sample Link
          </button>
        </div>
      </Space>
    </Card>
  );
};

export default ThemePreview;
